<template>
  <div :class="prefixCls">
    <div :class="classes" @click="$_onUploadClick" @dragleave.prevent="dragging = false" @dragover.prevent="dragging = true" @drop.prevent="$_onUploadDrop">
      <input ref="file" :accept="accept" :multiple="multiple" style="display: none" type="file" :name="name" @change="$_onUploadChange">
      <slot></slot>
    </div>
    <slot name="tip"></slot>
    <t-upload-list v-if="showUploaded" :files="files" @preview-file="$_onFilePreview" @remove-file="$_onFileRemove"></t-upload-list>
  </div>
</template>

<script>
import nanoid from 'nanoid/non-secure'
import TUploadList from './upload-list.vue'
import ajax from '../../utils/ajax'
import { PREFIX_CLASS } from '@/utils/constants'

const PREFIX_CLS = `${PREFIX_CLASS}upload`

export default {
  name: 'TUpload',
  components: {
    TUploadList
  },
  props: {
    action: {
      type: String,
      required: true
    },
    headers: {
      type: Object,
      default () {
        return {}
      }
    },
    multiple: {
      type: Boolean,
      default: false
    },
    data: {
      type: Object,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    auto: {
      type: Boolean,
      default: false
    },
    name: {
      type: String,
      default: 'file'
    },
    withCredentials: {
      type: Boolean,
      default: false
    },
    showUploaded: {
      type: Boolean,
      default: true
    },
    type: {
      type: String,
      default: 'select',
      validator (val) {
        /* istanbul ignore next */
        return ['select', 'drag'].includes(val)
      }
    },
    method: {
      type: String,
      default: 'POST',
      validator (val) {
        /* istanbul ignore next */
        return ['POST', 'PUT', 'GET'].includes(val)
      }
    },
    allowedExt: {
      type: Array,
      default () {
        return []
      }
    },
    accept: {
      type: String,
      default: ''
    },
    maxSize: {
      type: Number,
      default: 0
    },
    beforeUploadClick: {
      type: Function,
      default: null
    },
    beforeUpload: {
      type: Function,
      default: null
    },
    uploaded: {
      type: Array,
      default () {
        return []
      }
    },
    maxSizeTips: {
      type: String,
      default () {
        return `文件超出大小限制，最大值${this.maxSize}kb`
      }
      // },
      // onRemove: {
      //   type: Function,
      //   default() {
      //     return {}
      //   }
    }
  },
  data () {
    return {
      prefixCls: PREFIX_CLS,
      dragging: false,
      uplaodAuto: this.auto,
      files: [],
      ajaxObj: null
    }
  },
  computed: {
    classes () {
      return {
        [`${PREFIX_CLS}-select`]: this.type === 'select',
        [`${PREFIX_CLS}--drag`]: this.type === 'drag',
        [`${PREFIX_CLS}--drag__over`]: this.type === 'drag' && this.dragging
      }
    }
  },
  watch: {
    uploaded: {
      immediate: true,
      handler (newVal) {
        this.files = newVal.map(file => ({
          ...file,
          uuid: nanoid(),
          status: 'finished',
          percentage: 100
        }))
      }
    }
  },
  methods: {
    $_onUploadClick () {
      if (this.disabled) return
      if (this.beforeUploadClick) {
        let before = this.beforeUploadClick()
        if (before === true) {
          this.$refs.file.click()
        } else {
          this.$emit('cancel-select-file')
        }
      } else {
        this.$refs.file.click()
      }
    },
    $_onUploadChange (e) {
      let files = e.target.files
      if (!files) return
      this.$_uploadFiles(files)
      this.$refs.file.value = null
    },
    $_onUploadDrop (e) {
      this.dragging = false
      this.$_uploadFiles(e.dataTransfer.files)
    },
    $_uploadFiles (files) {
      let posts = Array.prototype.slice.call(files)
      if (!this.multiple) posts = posts.slice(0, 1)
      if (posts.length === 0) return
      posts.forEach(file => {
        this.$_upload(file)
      })
    },
    $_upload (file) {
      if (!this.beforeUpload) {
        return this.$_post(file)
      }
      let before = this.beforeUpload(file)
      if (before && typeof before.then === 'function') {
        before.then(processed => {
          if (
            Object.prototype.toString.call(processed) === '[object File]'
          ) {
            this.$_post(processed)
          } else {
            this.$_post(file)
          }
          return true
        }).catch(() => {
          return new Error('upload failure')
        })
      } else if (before) {
        this.$_post(file)
      } else {
        this.$emit('cancel')
      }
    },
    $_post (file) {
      const files = this.files.slice()
      if (this.allowedExt.length > 0) {
        const fileExt = file.name
          .split('.')
          .pop()
          .toLocaleLowerCase()
        const allowed = this.allowedExt.some(
          v => v.toLocaleLowerCase() === fileExt
        )
        if (!allowed) {
          this.$emit('file-ext-error', file, files)
          return false
        }
      }
      // check maxSize
      if (this.maxSize) {
        if (file.size > this.maxSize * 1024) {
          this.$Message.danger(this.maxSizeTips)
          this.$emit('file-exceeded-size', file, files)
          return false
        }
      }
      // if (this.maxSize > 0) {
      //   if (file.size > this.maxSize * 1024) {
      //     this.$emit('file-exceeded-size', file, files)
      //     return false
      //   }
      // }
      this.$_handleStart(file)
      if (this.uplaodAuto) {
        return
      }
      this.ajaxObj = ajax({
        headers: this.headers,
        withCredentials: this.withCredentials,
        file,
        data: this.data,
        filename: this.name,
        action: this.action,
        method: this.method,
        onProgress: e => {
          this.$_handleProgress(e, file)
        },
        onSuccess: ret => {
          this.$_handleSuccess(ret, file)
        },
        onError: (err, response) => {
          this.$_handleError(err, response, file)
        }
      })
    },
    $_getFile (file) {
      return this.files.filter(v => v.uuid === file.uuid)[0]
    },
    $_handleStart (file) {
      file.uuid = nanoid()
      this.files.push({
        status: 'uploading',
        name: file.name,
        size: file.size,
        percentage: 0,
        uuid: file.uuid,
        showProgress: true
      })
    },
    $_handleProgress (e, file) {
      const _file = this.$_getFile(file)
      this.$emit('progress', e, _file, this.files.slice())
      _file.percentage = e.percentage || 0
    },
    $_handleSuccess (ret, file) {
      const _file = this.$_getFile(file)
      if (_file) {
        _file.status = 'finished'
        _file.response = ret
        this.$emit('success', ret, _file, this.files.slice())
        setTimeout(() => {
          /* istanbul ignore next */
          _file.showProgress = false
        }, 1000)
      }
    },
    $_handleError (err, response, file) {
      const _file = this.$_getFile(file)
      _file.status = 'fail'
      this.$_removeFile(file)
      this.$emit('error', err, response, _file)
    },
    $_onFileRemove (file) {
      this.$_removeFile(file)
      this.$emit('remove-file', file, this.files.slice())
    },
    $_onFilePreview (file) {
      if (file.status === 'finished') {
        this.$emit('preview-file', file)
      }
    },
    $_removeFile (file) {
      this.files = this.files.filter(v => v.uuid !== file.uuid)
    },
    removeFile (file) {
      this.$_onFileRemove(file)
    },
    clearFiles () {
      this.files = []
    },
    getFiles () {
      return this.files.slice()
    },
    abort () {
      this.ajaxObj.stop()
      this.files.splice([this.files.length - 1], 1)
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/module/upload/upload.vue